import { useState, useEffect } from 'react';

export const useDeliveryLocation = (latestPCF, latestTCF) => {
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [mapUrl, setMapUrl] = useState(null);
  const [publicationDate, setPublicationDate] = useState(null); // New state

  useEffect(() => {
    // Helper to extract PublicationDate as ISO string (or null)
    const getPublicationDate = (doc) => {
      if (!doc?.value) return null;
      const pd = doc.value.find(v => v.idShort === 'PublicationDate')?.value;
      return pd ? pd : null; // assuming it's already an ISO string like "2025-12-08T12:00:00Z"
    };

    const tcfDate = latestTCF ? getPublicationDate(latestTCF) : null;
    const pcfDate = latestPCF ? getPublicationDate(latestPCF) : null;

    // Determine which document is newer (TCF preferred if equal)
    let sourceDoc = null;
    let chosenPublicationDate = null;

    if (tcfDate && (!pcfDate || tcfDate >= pcfDate)) {
      sourceDoc = latestTCF;
      chosenPublicationDate = tcfDate;
    } else if (pcfDate) {
      sourceDoc = latestPCF;
      chosenPublicationDate = pcfDate;
    }
    // else both null → sourceDoc remains null

    setPublicationDate(chosenPublicationDate);

    // If no valid source document, clear everything
    if (!sourceDoc) {
      setDeliveryAddress('');
      setMapUrl(null);
      return;
    }

    // Determine which address block to read based on source
    const addressIdShort =
      sourceDoc === latestTCF
        ? 'TCFGoodsTransportAddressTakeover'
        : 'PCFGoodsAddressHandover';

    const addressBlock = sourceDoc.value?.find(v => v.idShort === addressIdShort);

    if (!addressBlock?.value) {
      setDeliveryAddress('');
      setMapUrl(null);
      return;
    }

    // Helper to get submodel element values
    const get = (idShort) =>
      addressBlock.value.find(x => x.idShort === idShort)?.value || '';

    const street = get('Street');
    const houseNumber = get('HouseNumber');
    const zipCode = get('ZipCode');
    const city = get('CityTown');
    const country = get('Country') || 'Germany';
    const lat = get('Latitude');
    const lon = get('Longitude');

    // Format display address
    const parts = [];
    if (street) parts.push(street + (houseNumber ? ` ${houseNumber}` : ''));
    if (zipCode && city) parts.push(`${zipCode} ${city}`);
    else if (city) parts.push(city);
    if (country && country.toLowerCase() !== 'germany') parts.push(country);

    const formatted = parts.length > 0 ? parts.join(', ') : 'N/A';
    setDeliveryAddress(formatted);

    // Build map URL
    let query = '';
    if (street || houseNumber || zipCode || city || country) {
      query = [street, houseNumber, zipCode, city, country]
        .filter(Boolean)
        .join(', ');
    } else if (lat && lon) {
      query = `${lat},${lon}`;
    }

    setMapUrl(query
      ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`
      : null
    );
  }, [latestTCF, latestPCF]);

  return { deliveryAddress, mapUrl, publicationDate };
};